import { useState, useEffect } from "react";
import {
  AppBar,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, NavLink, useLocation } from "react-router-dom";

const pages = ["About", "Contact", "Project List"];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 2),
  ...theme.mixins.toolbar,
  justifyContent: "space-between",
  background: "#081A28",
}));

const drawerWidth = 260;

function MenuNavbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Keep the mobile drawer in sync with route changes, including navigation
  // started from links outside the drawer.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={scrolled ? 6 : 0}
        sx={{
          background: scrolled
            ? "linear-gradient(90deg, #081A28 0%, #0f2d45 100%)"
            : "linear-gradient(90deg, #081A28 0%, #0f2d45 100%)",
          borderBottom: "2px solid #D4AE60",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
            {/* Mobile hamburger */}
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" }, alignItems: "center" }}>
              <IconButton
                size="large"
                aria-label="Open navigation menu"
                onClick={handleDrawerOpen}
                sx={{ color: "white", p: 1 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Mobile logo */}
            <Typography
              noWrap
              component="div"
              sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", flexGrow: 1 }}
            >
              <Link to="/">
                <img
                  src="/photos/logo/logoBackgroundTrans.png"
                  style={{ width: 140, height: 52, objectFit: "cover" }}
                  alt="Logo GGSinergi"
                  loading="lazy"
                />
              </Link>
            </Typography>

            {/* Desktop logo */}
            <Typography
              noWrap
              component="div"
              sx={{ flexGrow: 1, display: { xs: "none", md: "flex" }, alignItems: "center" }}
            >
              <Link to="/">
                <img
                  src="/photos/logo/logoBackgroundTrans.png"
                  style={{ width: 160, height: 58, objectFit: "cover" }}
                  alt="Logo GGSinergi"
                  loading="lazy"
                />
              </Link>
            </Typography>

            {/* Desktop menu */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 1,
              }}
            >
              {pages.map((page, i) => (
                <NavLink
                  className={({ isActive }) =>
                    isActive ? "text-active" : "text-hover"
                  }
                  key={i}
                  to={`/${page.toLowerCase().split(" ").join("-")}`}
                  style={styles.menuText}
                >
                  {page}
                </NavLink>
              ))}
              <a
                className="text-hover"
                href="https://admin.ggsinergi.com/login"
                style={{
                  ...styles.menuText,
                  background: "linear-gradient(135deg, #D4AE60, #b8923a)",
                  color: "#081A28",
                  padding: "8px 20px",
                  borderRadius: "2px",
                  marginLeft: "8px",
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Login
              </a>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: "#f8f7f4",
          },
        }}
        variant="temporary"
        anchor="left"
        onClose={handleDrawerClose}
        open={open}
      >
        <DrawerHeader>
          <Link to="/" onClick={handleDrawerClose}>
            <img
              src="/photos/logo/logoBackgroundTrans.png"
              style={{ width: 140, height: 48, objectFit: "cover" }}
              alt="Logo GGSinergi"
              loading="lazy"
            />
          </Link>
          <IconButton
            type="button"
            aria-label="Close navigation menu"
            onClick={handleDrawerClose}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
        <Box sx={{ borderBottom: "3px solid #D4AE60" }} />
        <List sx={{ pt: 2 }}>
          {pages.map((link, index) => (
            <ListItemButton
              component={Link}
              to={`/${link.toLowerCase().split(" ").join("-")}`}
              key={index}
              onClickCapture={handleDrawerClose}
              sx={{
                py: 1.5,
                px: 3,
                "&:hover": { backgroundColor: "#f0ece3", color: "#005B3B" },
              }}
            >
              <ListItemText
                sx={{ color: "#081A28" }}
                primaryTypographyProps={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 600,
                  fontSize: "15px",
                  letterSpacing: "0.5px",
                }}
                primary={link}
              />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 1 }} />
          <ListItemButton
            component="a"
            href="https://admin.ggsinergi.com/login"
            target="_blank"
            rel="noopener noreferrer"
            onClickCapture={handleDrawerClose}
            sx={{
              py: 1.5,
              px: 3,
              "&:hover": { backgroundColor: "#f0ece3" },
            }}
          >
            <ListItemText
              primaryTypographyProps={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "15px",
                color: "#D4AE60",
                letterSpacing: "0.5px",
              }}
              primary="Login"
            />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}

const styles = {
  menuText: {
    padding: "6px 14px",
    color: "white",
    fontSize: "14px",
    fontWeight: "700",
    textDecoration: "none",
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: "1px",
    textTransform: "uppercase",
  },
};

export default MenuNavbar;
